import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileSearch, Menu, Receipt } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { toast } from "sonner";
import { Sidebar } from "../components/Sidebar";
import { StatementHeader } from "../components/StatementHeader";
import { TransactionTable } from "../components/TransactionTable";
import { DetailPanel } from "../components/DetailPanel";
import { FilterBar, EMPTY_FILTERS, applyFilters } from "../components/FilterBar";
import Inbox from "../components/Inbox";
import Overview from "../components/Overview";
import AgentSettings from "../components/AgentSettings";
import AgentActivity from "../components/AgentActivity";
import BankingSettings from "../components/BankingSettings";
import { api, errMsg } from "../lib/api";

const Welcome = () => (
  <div data-testid="welcome" className="mx-auto mt-16 max-w-xl px-6 fade-up">
    <FileSearch className="h-10 w-10 text-slate-400" />
    <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">Jeder Buchung ihr Beleg.</h1>
    <p className="mt-4 text-base leading-relaxed text-slate-600">
      Laden Sie einen Kontoauszug Ihrer Erste Bank (PDF) hoch. BelegFinder liest alle Buchungen aus, prüft den Saldo und erklärt jede Buchung – inklusive Hinweis, wo Sie den Beleg finden.
    </p>
  </div>
);

const useMedia = (q) => {
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const h = () => setM(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, [q]);
  return m;
};

const MobileBar = ({ onMenu }) => (
  <div className="sticky top-0 z-30 flex items-center gap-3 border-b border-slate-200 bg-[#F3F2EE] px-3 py-2" data-testid="mobile-topbar">
    <button onClick={onMenu} className="flex h-11 w-11 items-center justify-center rounded-lg hover:bg-white" aria-label="Menü öffnen" data-testid="mobile-menu-btn">
      <Menu className="h-5 w-5" />
    </button>
    <div className="flex items-center gap-2 font-display text-lg font-extrabold tracking-tight text-slate-900"><Receipt className="h-5 w-5 text-amber-600" />BelegFinder</div>
  </div>
);

const isTyping = (e) => {
  const t = e.target;
  return ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable || document.querySelector("[role=dialog],[role=alertdialog]");
};

export default function Workspace({ user, onLogout }) {
  const [statements, setStatements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState("overview");
  const [txs, setTxs] = useState([]);
  const [txId, setTxId] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [autoJump, setAutoJumpState] = useState(localStorage.getItem("bf-autojump") !== "0");
  const [inboxCount, setInboxCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const detailRef = useRef(null);
  const pendingFirstOpen = useRef(false);

  const setAutoJump = (v) => { setAutoJumpState(v); localStorage.setItem("bf-autojump", v ? "1" : "0"); };
  const current = statements.find((s) => s.id === selectedId);

  const loadStatements = useCallback(async () => {
    const [list, ov] = await Promise.all([api.listStatements(), api.overview()]);
    setStatements(list);
    setInboxCount(ov.receipts_unassigned);
    return list;
  }, []);

  const loadTxs = useCallback(async (sid) => {
    if (!sid) { setTxs([]); return []; }
    const list = await api.listTransactions(sid);
    setTxs(list);
    return list;
  }, []);

  useEffect(() => {
    loadStatements().then((l) => {
      const p = new URLSearchParams(window.location.search);
      if (p.get("view") === "banking" || p.get("view") === "agent") { setView(p.get("view")); return; }
      const target = p.get("statement") ? l.find((s) => s.id === p.get("statement")) : p.get("year") ? l.find((s) => String(s.statement_year) === p.get("year"))
        : (p.get("status") || p.get("q") || p.get("min")) ? l.find((s) => s.status === "ready") : null;
      if (target) {
        setFilters({ ...EMPTY_FILTERS, onlyOpen: p.get("status") === "offen", status: p.get("status") && p.get("status") !== "offen" ? p.get("status") : "alle", q: p.get("q") || "", min: p.get("min") || "" });
        setView("statement"); setSelectedId(target.id);
        window.history.replaceState(null, "", window.location.pathname);
      } else if (!l.length) setView("welcome");
    });
  }, [loadStatements]);

  useEffect(() => {
    setTxId(null);
    loadTxs(selectedId).then((list) => {
      if (pendingFirstOpen.current) {
        pendingFirstOpen.current = false;
        const first = list.find((t) => t.status === "offen");
        if (first) setTxId(first.id);
      }
    });
  }, [selectedId, loadTxs]);

  const busy = statements.some((s) => s.status === "processing" || s.analysis?.status === "running");
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => { loadStatements(); loadTxs(selectedId); }, 2500);
    return () => clearInterval(t);
  }, [busy, selectedId, loadStatements, loadTxs]);

  const refresh = () => { loadStatements(); loadTxs(selectedId); setRefreshKey((k) => k + 1); };
  const filtered = useMemo(() => applyFilters(txs, filters), [txs, filters]);
  useEffect(() => { if (refreshKey) loadTxs(selectedId); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectTx = useCallback((id) => {
    setTxId(id);
    setTimeout(() => {
      document.querySelector(`[data-testid="transaction-row-${id}"]`)?.scrollIntoView({ block: "nearest" });
    }, 50);
  }, []);

  const gotoNextOpen = useCallback((list, fromId) => {
    const idx = list.findIndex((t) => t.id === fromId);
    const order = [...list.slice(idx + 1), ...list.slice(0, idx + 1)];
    const next = order.find((t) => t.status === "offen" && t.id !== fromId);
    if (next) return selectTx(next.id);
    const other = statements.find((s) => s.id !== selectedId && s.status === "ready" && s.progress?.open > 0);
    if (other) {
      pendingFirstOpen.current = true;
      setSelectedId(other.id);
      toast(`Weiter mit Auszug ${other.statement_number || ""}`);
      return;
    }
    toast.success("Alles erledigt – keine offenen Buchungen mehr.");
  }, [statements, selectedId, selectTx]);

  const onStatus = async (tx, status) => {
    try {
      const t = await api.setStatus(tx.id, status);
      const list = txs.map((x) => (x.id === t.id ? t : x));
      setTxs(list);
      loadStatements();
      if (autoJump && status !== "offen") gotoNextOpen(list, tx.id);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onConfirmed = async (tid) => {
    const list = await loadTxs(selectedId);
    loadStatements();
    if (autoJump) gotoNextOpen(list, tid);
  };

  useEffect(() => {
    if (view !== "statement") return;
    const h = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e)) return;
      const k = e.key.toLowerCase();
      if (!["j", "k", "n"].includes(k)) return;
      e.preventDefault();
      if (k === "n") return gotoNextOpen(filtered.length ? filtered : txs, txId);
      const idx = filtered.findIndex((t) => t.id === txId);
      const next = k === "j" ? filtered[Math.min(filtered.length - 1, idx + 1)] : filtered[Math.max(0, idx - 1)];
      if (next) selectTx(next.id);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [view, filtered, txs, txId, gotoNextOpen, selectTx]);

  const openStatement = (id) => { setView("statement"); setSelectedId(id); loadStatements(); };
  const tx = txs.find((t) => t.id === txId);
  const showDetail = view === "statement" && current?.status === "ready";
  const wide = useMedia("(min-width: 1440px)");
  const desktop = useMedia("(min-width: 1024px)");
  const [menu, setMenu] = useState(false);
  const detail = (
    <DetailPanel
      tx={tx}
      onClose={() => setTxId(null)}
      onChanged={(t) => { setTxs((list) => list.map((x) => (x.id === t.id ? t : x))); loadStatements(); }}
      onRefresh={() => { loadTxs(selectedId); loadStatements(); }}
      onStatus={(s) => onStatus(tx, s)}
      onConfirmed={() => onConfirmed(tx.id)}
      onDeleted={() => { setTxId(null); refresh(); }}
    />
  );
  const sidebar = (inSheet) => (
    <Sidebar
      inSheet={inSheet}
      statements={statements}
      selectedId={view === "statement" ? selectedId : null}
      view={view}
      inboxCount={inboxCount}
      onView={(v) => { setView(v); refresh(); setMenu(false); }}
      onSelect={(id) => { openStatement(id); setMenu(false); }}
      onUploaded={(st) => { loadStatements(); openStatement(st.id); setMenu(false); }}
      user={user}
      onLogout={onLogout}
    />
  );

  return (
    <div className="flex h-screen flex-col overflow-hidden lg:flex-row" data-testid="app-root">
      {desktop ? sidebar(false) : (
        <>
          <MobileBar onMenu={() => setMenu(true)} />
          <Sheet open={menu} onOpenChange={setMenu}>
            <SheetContent side="left" hideClose className="w-[300px] max-w-[85vw] p-0 sm:max-w-[300px]" aria-describedby={undefined} data-testid="mobile-menu-sheet">
              <SheetTitle className="sr-only">Menü</SheetTitle>
              {sidebar(true)}
            </SheetContent>
          </Sheet>
        </>
      )}
      <main className="thin-scroll min-w-0 flex-1 overflow-y-auto overflow-x-hidden" data-testid="main-panel">
        {view === "welcome" && <Welcome />}
        {view === "overview" && (
          <Overview refreshKey={refreshKey} onOpenInbox={() => setView("inbox")} onChanged={refresh}
            onOpenYear={(y) => { const s = statements.find((x) => x.statement_year === y); if (s) openStatement(s.id); }} />
        )}
        {view === "agent" && <AgentSettings />}
        {view === "banking" && <BankingSettings />}
        {view === "activity" && <AgentActivity onDataChanged={() => { loadStatements(); loadTxs(selectedId); }} />}
        {view === "inbox" && <Inbox onDataChanged={() => { loadStatements(); loadTxs(selectedId); }} />}
        {view === "statement" && current && (
          <div className="grid gap-5 p-4 sm:p-6 lg:p-8">
            <StatementHeader
              st={current}
              onChanged={refresh}
              onSelect={openStatement}
              onDeleted={() => { setSelectedId(null); setView("overview"); refresh(); }}
              onAdded={(t) => { refresh(); setTxId(t.id); }}
            />
            {current.status === "ready" && (
              <>
                <FilterBar f={filters} set={setFilters} shown={filtered.length} total={txs.length}
                  onNextOpen={() => gotoNextOpen(txs, txId)} autoJump={autoJump} setAutoJump={setAutoJump} />
                <TransactionTable transactions={filtered} selectedId={txId} onSelect={selectTx} />
              </>
            )}
          </div>
        )}
      </main>
      {showDetail && wide && (
        <aside ref={detailRef} className="thin-scroll h-screen w-[460px] shrink-0 overflow-y-auto border-l border-slate-200 bg-[#F6F5F1]" data-testid="detail-aside">
          {detail}
        </aside>
      )}
      {showDetail && !wide && (
        <Sheet open={!!tx} onOpenChange={(o) => { if (!o) setTxId(null); }}>
          <SheetContent side="right" hideClose aria-describedby={undefined} data-testid="detail-sheet"
            className={`thin-scroll overflow-y-auto bg-[#F6F5F1] p-0 ${desktop ? "w-[480px] max-w-[60vw] sm:max-w-[60vw]" : "w-full max-w-none sm:max-w-none"}`}>
            <SheetTitle className="sr-only">Buchung</SheetTitle>
            {tx && detail}
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
